<template>
  <ak-sidebar-layout>
    <template #sidebar>
      <ak-folders-sidebar
        ref="folders"
        v-model:active-folder="activeFolder"
        :active-account="activeAccount"
        :active-agency="activeAgency"
        :show-sort="true"
        class="bg-white"
      >
        <template #beforeFolders>
          <h4 class="color-grey">{{ $t('Automations') }}</h4>
          <ul class="folder-list list-bare mb">
            <template v-for="filter in workflowFilters" :key="filter.type">
              <li
                v-if="
                  filter.restrict === false || display('show', filter.restrict)
                "
                class="folder-list__item cursor-pointer"
                :class="{ 'is-active': activeFilter === filter.type }"
                @click.stop.prevent="activeFilter = filter.type"
              >
                <ak-icon
                  class="folder-list__item-indicator"
                  :type="filter.icon"
                  size="default"
                  :color="
                    activeFilter === filter.type ? filter.activeColor : ''
                  "
                />
                <div class="folder-list__item-body">
                  {{ filter.text }}
                  <em>({{ stats[filter.type] }})</em>
                </div>
              </li>
            </template>
          </ul>
        </template>
        <template #afterFolders>
          <div v-if="display('show', 'superadmin,developer')" class="mt-large">
            <h4 class="color-grey">{{ $t('Admin Views') }}</h4>
            <ak-button
              color="grey-lighter"
              size="small"
              tag="router-link"
              :to="{ name: 'automationGlobalHistory' }"
            >
              {{ $t('History') }}
              <ak-icon type="history" class="ml-tiny" />
            </ak-button>
            <ak-button
              color="grey-lighter"
              class="ml-tiny"
              size="small"
              tag="router-link"
              :to="{ name: 'automationLogs' }"
            >
              {{ $t('Logs') }}
              <ak-icon type="stopwatch" class="ml-tiny" />
            </ak-button>
          </div>
        </template>
      </ak-folders-sidebar>
    </template>
    <template #body>
      <div class="h-full one-whole overflow-y has-fab-bottom">
        <div class="px py-small">
          <h4 class="color-grey mb-none text-capitalize">
            {{ activeFolder }} {{ $t('Automations') }}
          </h4>
        </div>
        <div class="px pt-small">
          <ak-form @submit="fetchAutomations">
            <ak-pack>
              <ak-pack-item v-if="automations.length > 0">
                <ak-input
                  v-model="searchFilter"
                  :loading="loading"
                  :placeholder="$t('Search Workflows')"
                  type="search"
                  icon="search"
                  @right-icon-click="fetchAutomations"
                />
              </ak-pack-item>
              <ak-pack-item grow></ak-pack-item>
              <ak-pack-item right>
                <ak-button-group ghost class="bg-white">
                  <ak-button
                    :ghost="currentView !== 'list'"
                    color="grey-light"
                    single-icon
                    @click.stop.prevent="setView('list')"
                  >
                    <ak-icon type="list" />
                  </ak-button>
                  <ak-button
                    :ghost="currentView !== 'grid'"
                    color="grey-light"
                    single-icon
                    @click.stop.prevent="setView('grid')"
                  >
                    <ak-icon type="grid" />
                  </ak-button>
                </ak-button-group>
              </ak-pack-item>
            </ak-pack>
          </ak-form>
        </div>
        <list-view
          v-if="currentView === 'list'"
          v-model:data="automations"
          v-model:selected="selected"
          :total="total"
          :loading="loading"
          :active-page="currentPage"
          @page="onPage"
          @toggle="onToggle"
        />
        <grid-view
          v-if="currentView === 'grid'"
          v-model:data="automations"
          v-model:selected="selected"
          :total="total"
          :loading="loading"
          :active-page="currentPage"
          @page="onPage"
          @toggle="onToggle"
        />
        <ak-fab
          tag="router-link"
          :to="{ name: 'automationNew' }"
          icon="plus"
          position-x="center"
          position-y="bottom"
          style="margin-left: 140px"
        />
      </div>
      <bulk-actions
        :automations="selected"
        :account-id="activeAccount._id"
        :agency-id="activeAgency._id"
      />
      <clone-automation-modal
        v-model:is-active="showCloneModal"
        :data="currentCloneAutomation"
        :agency-id="activeAgency._id"
        :account-id="activeAccount._id"
        :user="userProfile"
      />
    </template>
  </ak-sidebar-layout>
</template>

<script>
import {
  defineComponent,
  ref,
  onMounted,
  onBeforeMount,
  computed,
  watch,
  provide,
} from 'vue'
import SidebarLayout from '@akerolabs/akero-vue/src/components/sidebar/SidebarLayout.vue'
import Folders from '../folders/Folders.vue'
import Icon from '@akerolabs/akero-vue/src/components/icon/Icon.vue'
import Pack from '@akerolabs/akero-vue/src/components/pack/Pack.vue'
import PackItem from '@akerolabs/akero-vue/src/components/pack/PackItem.vue'
import Form from '@akerolabs/akero-vue/src/components/form/Form.vue'
import Field from '@akerolabs/akero-vue/src/components/field/Field.vue'
import ButtonGroup from '@akerolabs/akero-vue/src/components/buttonGroup/ButtonGroup.vue'
import Button from '@akerolabs/akero-vue/src/components/button/Button.vue'
import Fab from '@akerolabs/akero-vue/src/components/fab/Fab.vue'
import Input from '@akerolabs/akero-vue/src/components/input/Input.vue'
import ListView from './automationComponents/ListView.vue'
import GridView from './automationComponents/GridView.vue'
import BulkActions from './automationComponents/BulkActions.vue'
import CloneAutomationModal from './automationComponents/CloneAutomationModal.vue'
import { sleep } from '@akerolabs/akero-vue/src/utils/helpers'
import { request } from '@akerolabs/akero-vue/src/utils/request'
import { success, error } from './utils'
import {
  userLevelIsHigerThan,
  display,
} from '@akerolabs/akero-vue/src/services/users'

const workflowFilters = [
  {
    text: 'All',
    type: 'all',
    activeColor: 'grey',
    icon: 'filing',
    restrict: false,
  },
  {
    text: 'Enabled',
    type: 'enabled',
    activeColor: 'quaternary',
    icon: 'flash',
    restrict: false,
  },
  {
    text: 'Disabled',
    type: 'disabled',
    activeColor: 'warning',
    icon: 'alert-circled',
    restrict: false,
  },
  {
    text: 'Triggered Manually',
    type: 'manual',
    activeColor: 'success',
    icon: 'arrow-right-b',
    restrict: false,
  },
  {
    text: 'Never Triggered',
    type: 'never',
    activeColor: 'danger',
    icon: 'close',
    restrict: false,
  },
  {
    text: 'Hidden',
    type: 'hidden',
    activeColor: 'grey',
    icon: 'eye-disabled',
    restrict: 'developer,superadmin,agencyadmin,agencyuser',
  },
]
export default defineComponent({
  name: 'AkAutomationList',
  components: {
    [SidebarLayout.name]: SidebarLayout,
    [Folders.name]: Folders,
    [Icon.name]: Icon,
    [Pack.name]: Pack,
    [PackItem.name]: PackItem,
    [Form.name]: Form,
    [Field.name]: Field,
    [ButtonGroup.name]: ButtonGroup,
    [Button.name]: Button,
    [Fab.name]: Fab,
    [Input.name]: Input,
    [ListView.name]: ListView,
    [GridView.name]: GridView,
    [BulkActions.name]: BulkActions,
    [CloneAutomationModal.name]: CloneAutomationModal,
  },
  props: {
    /**
     * @type {Object}
     * @description The active account (drilled in or users)
     */
    activeAccount: {
      default: () => ({}),
      type: Object,
    },

    /**
     * @type {Object}
     * @description The active agency (drilled in or users)
     */
    activeAgency: {
      default: () => ({}),
      type: Object,
    },

    /**
     * @type {Object}
     * @description The users profile data
     */
    userProfile: {
      default: () => ({}),
      type: Object,
    },
    /**
     * @type {Function}
     * @description Update the account automation view state
     */
    setState: {
      type: Function,
      default: undefined,
    },
  },
  setup(props) {
    const folders = ref(null)
    const activeFilter = ref('all')
    const activeFolder = ref('all')
    const loading = ref(true)
    const searchFilter = ref('')
    const currentView = ref('list')
    const currentPage = ref(1)
    const total = ref(0)
    const automations = ref([])
    const selected = ref([])
    const stats = ref({
      all: 0,
      enabled: 0,
      disabled: 0,
      manual: 0,
      never: 0,
      hidden: 0,
    })
    const filter = ref({})
    const showCloneModal = ref(false)
    const currentCloneAutomation = ref({})
    /**
     * @description watch the filters in the sidebar so the filter can be created
     */
    watch(activeFilter, async (value) => {
      switch (value) {
        case 'enabled':
          filter.value = { status: 'enabled' }
          break
        case 'disabled':
          filter.value = { status: 'disabled' }
          break
        case 'manual':
          filter.value = {
            triggers: JSON.stringify({ $exists: true, $eq: [] }),
          }
          break
        case 'never':
          filter.value = { triggered: JSON.stringify({ $eq: 0 }) }
          break
        case 'hidden':
          filter.value = { hidden: true }
          break
        default:
          filter.value = {}
          break
      }
      await fetchAutomations()
    })

    /**
     * @description when the folder changes we want to re-run the
     * fetch to apply the folder filter to the query
     */
    watch(activeFolder, async () => {
      await fetchAutomations()
    })

    /**
     * @description Get a single stat from a stats array
     * @param {Array} stats
     * @param {String} key
     */
    const getStat = (stats, key) => {
      const obj = stats.find((stat) => stat._id === key)
      if (obj) {
        return obj.count
      }
      return 0
    }

    /**
     * @description Get the automation stats for the sidebar
     */
    const fetchStats = async () => {
      const statsRequest = {
        params: {
          blacklist: 'allowHidden, showAutoresponder',
          agency: props.activeAgency._id,
          account: props.activeAccount._id,
          allowHidden: false,
          'autoresponder.isForApp': JSON.stringify({ $ne: true }),
          overrideAccess: true,
        },
      }
      if (userLevelIsHigerThan('agencyuser')) {
        statsRequest.params.allowHidden = true
      }
      if (userLevelIsHigerThan('developer')) {
        delete statsRequest.params['autoresponder.isForApp']
      }
      try {
        const { data } = await request.get('/tasks/stats/status', statsRequest)
        stats.value.enabled = getStat(data.data, 'enabled')
        stats.value.disabled = getStat(data.data, 'disabled')
        stats.value.manual = getStat(data.data, 'triggerless')
        stats.value.never = getStat(data.data, 'neverTriggered')
        stats.value.hidden = getStat(data.data, 'hidden')
        let all = 0
        const excludeList = ['hidden', 'triggerless', 'neverTriggered']
        data.data.forEach(({ _id, count }) => {
          if (!excludeList.includes(_id)) {
            all += count
          }
        })
        stats.value.all = all
      } catch (ex) {
        error('Failed to get stats', ex)
      }
    }

    /**
     * @description Set the view type to list or grid
     * @param {String} view
     */
    const setView = (view) => {
      currentView.value = view
      props.setState('automation.workflows.view', view)
    }

    /**
     * @description Create and object ready for requesting data from the api
     * @type {Object}
     */
    const automationRequest = computed(() => {
      const query = {
        params: {
          account: props.activeAccount._id,
          agency: props.activeAgency._id,
          overrideAccess: true,
          'autoresponder.isForApp': JSON.stringify({ $ne: true }),
          hidden: JSON.stringify({ $ne: true }),
          deleted: false,
          per_page: 20,
          page: currentPage.value,
        },
      }
      if (userLevelIsHigerThan('agencyuser')) {
        delete query.params.hidden
      }
      if (userLevelIsHigerThan('developer')) {
        delete query.params['autoresponder.isForApp']
      }
      // add the filter after the user level check have been done so that filters don't get removed
      query.params = { ...query.params, ...filter.value }
      if (searchFilter.value) {
        query.params.name = JSON.stringify({
          $regex: searchFilter.value,
          $options: 'i',
        })
      }
      if (activeFolder.value && activeFolder.value !== 'all') {
        query.params.folder = activeFolder.value
      }
      return query
    })

    /**
     * @description Fetch data from the api
     */
    const fetchAutomations = async () => {
      loading.value = true
      const query = { ...automationRequest.value }
      let tableData = []
      let totalNum = 0
      let page = 1
      try {
        const { data } = await request.get('/tasks', query)
        tableData = data.data
        totalNum = data.meta.pagination.total
        page = data.meta.pagination.current
      } catch (ex) {
        error('Failed to get Workflows', ex)
      }
      automations.value = tableData
      currentPage.value = page
      total.value = totalNum
      selected.value = []
      loading.value = false
    }

    /**
     * @description When the pagination page changes
     * @param {Number} page
     */
    const onPage = async (page) => {
      currentPage.value = page
      await fetchAutomations()
    }

    /**
     * @description toggle enabled / disabled for an automation
     * @param {Object} automation
     */
    const onToggle = async (automation) => {
      await sleep(10)
      const { _id, status, name } = automation
      try {
        await request.put(`/tasks/${_id}`, { status })
        success(`${name} is now ${status}`)
        await fetchStats()
      } catch (ex) {
        error('Failed to update Workflow')
      }
    }

    /**
     * @param {Object} automation The automation to be cloned
     * @description Set the automation to be cloned and open the modal
     */
    const openCloneModal = (automation) => {
      currentCloneAutomation.value = automation
      showCloneModal.value = true
    }

    /**
     * @description Provide the open modal function so it can be used by child components
     */
    provide('openCloneModal', openCloneModal)

    /**
     * @description Provide the fetch function so it can be used by child components
     */
    provide('fetchAutomations', fetchAutomations)

    /**
     * @description Provide the fetch function so it can be used by child components
     */
    provide('fetchStats', fetchStats)

    /**
     * @description Before the component is mounted update the view type from the user profile
     */
    onBeforeMount(() => {
      currentView.value = props.userProfile.states.automation.workflows.view
    })

    /**
     * @description On mounted run the first fetch functions
     */
    onMounted(async () => {
      await fetchStats()
      await fetchAutomations()
    })

    return {
      folders,
      activeFolder,
      workflowFilters,
      activeFilter,
      stats,
      display,
      loading,
      searchFilter,
      fetchAutomations,
      currentView,
      setView,
      automations,
      total,
      selected,
      onPage,
      onToggle,
      showCloneModal,
      currentCloneAutomation,
      currentPage,
    }
  },
})
</script>

<i18n>
{
  "en": {
    "Automations": "Automations",
    "Admin Views": "Admin Views",
    "History": "History",
    "Logs": "Logs",
    "Search Workflows": "Search Workflows"
  },
  "en-US": {
    "Automations": "Automations",
    "Admin Views": "Admin Views",
    "History": "History",
    "Logs": "Logs",
    "Search Workflows": "Search Workflows"
  }
}
</i18n>
